import {
  useState,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";

import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { useToast } from "hooks/toast";

import { hideLoading, showLoading } from "store/modules/loading/actions";
import { useAppSelector } from "store/rootReducer";

import { ChangePassword } from "components/ChangePassword";
import PhotoWebCam from "components/PhotoWebCam";

import { Container } from "./styles";

interface IMyAccount {
  setOpenDrawer: React.Dispatch<React.SetStateAction<boolean>>;
}

interface ChangePasswordRef {
  handleSubmitNewPassword: () => Promise<void>;
}

interface UploadImageRef {
  handleUploadImage: () => Promise<void>;
}

export interface MyAccountRef {
  handleSubmitForm: () => Promise<void>;
}

export const MyAccount = forwardRef<MyAccountRef, IMyAccount>(
  ({ setOpenDrawer }, ref) => {
    const { t } = useTranslation();
    const { addToast } = useToast();
    const [passwordOld, setPasswordOld] = useState("");
    const [password, setPassword] = useState("");
    const [passwordConfirm, setPasswordConfirm] = useState("");

    const changePasswordRef = useRef<ChangePasswordRef | null>(null);
    const uploadImageRef = useRef<UploadImageRef | null>(null);
    const [hasImageChange, setHasImageChange] = useState(false);

    const userProfile = useAppSelector((state) => state.profile);

    const dispatch = useDispatch();

    const handleSubmitForm = useCallback(async () => {
      if (!passwordOld && !password && !passwordConfirm && !hasImageChange)
        return;

      dispatch(showLoading());

      const wantsPassword = Boolean(passwordOld || password || passwordConfirm);
      const wantsImage = hasImageChange;

      let passwordSuccess = false;
      let imageSuccess = false;

      if (wantsPassword) {
        try {
          await changePasswordRef.current!.handleSubmitNewPassword();
          passwordSuccess = true;
        } catch {
          //
        }
      }

      if (wantsImage) {
        try {
          await uploadImageRef.current!.handleUploadImage();
          imageSuccess = true;
        } catch {
          addToast({
            type: "error",
            title: t("account.uploadFailed"),
            description: t("account.photoUploadError"),
          });
        }
      }

      dispatch(hideLoading());

      const close =
        (!wantsPassword && imageSuccess) ||
        (!wantsImage && passwordSuccess) ||
        (wantsPassword && wantsImage && passwordSuccess && imageSuccess);

      if (close) {
        setOpenDrawer(false);
      }
    }, [
      passwordOld,
      password,
      passwordConfirm,
      hasImageChange,
      dispatch,
      addToast,
      setOpenDrawer,
    ]);

    useImperativeHandle(ref, () => ({
      handleSubmitForm,
    }));

    return (
      <Container>
        <PhotoWebCam
          ref={uploadImageRef}
          containerName={userProfile.containerName}
          onImageChange={() => setHasImageChange(true)}
        />
        <ChangePassword
          isLogged
          oldPassword={passwordOld}
          setOldPassword={setPasswordOld}
          newPassword={password}
          setNewPassword={setPassword}
          confirmPassword={passwordConfirm}
          setConfirmPassword={setPasswordConfirm}
          ref={changePasswordRef}
        />
      </Container>
    );
  },
);

MyAccount.displayName = "MyAccount";
